import { Flex, Text, UnorderedList, ListItem, Avatar, Button } from "@chakra-ui/react";
import moment from "moment";
import { Link } from "react-router-dom";

//Declare all possible types of notifications here
const NOTIFICATION_KEYS = [
  "borrower_registered",
  "created_draw_request",
  "created_item_contact",
  "created_loan",
  "created_loan_contact",
  "deleted_loan_contact",
  "invited_borrower",
  "invited_lender",
  "kyc_verified",
  "onboarded",
  "requested_funds",
  "sent_message",
  "updated_draw_request",
  "updated_loan",
  "uploaded_document",
  "verified_funds",
  "updated_signature",
  "draw_due_soon",
] as const;

export type NotificationKey = (typeof NOTIFICATION_KEYS)[number];

const NOTIFICATION_TEXTS: Record<NotificationKey, string> = {
  borrower_registered: "Borrower Registered",
  created_draw_request: "Draw Request Created",
  created_item_contact: "Assigned Checklist Item",
  created_loan: "Loan Created",
  created_loan_contact: `You've been assigned to a loan!`,
  deleted_loan_contact: `You've been unassigned from a loan.`,
  invited_borrower: "Invited Borrower",
  invited_lender: "Invited Lender",
  kyc_verified: `Borrower Account Verified`,
  onboarded: `Borrower Onboarded`,
  requested_funds: `Proof of Funds Requested`,
  sent_message: "Sent Message",
  updated_draw_request: `Draw Request Updated`,
  updated_loan: `A loan has been updated.`,
  uploaded_document: "Uploaded Document",
  verified_funds: `Proof of Funds Verified`,
  updated_signature: `Signature Updated`,
  draw_due_soon: `Draw Due Soon`,
};

export const getKeyFromName = (name: string): NotificationKey => {
  if (NOTIFICATION_KEYS.includes(name as NotificationKey)) {
    console.debug("Notification key found for name: " + name);
    return name as NotificationKey;
  }
  // If the name is not found, try to find the key without the last part
  const nameParts = name.split("_");
  const shortName = nameParts.slice(0, nameParts.length - 1).join("_");
  console.debug("Trying to find notification key for name: " + shortName);
  if (NOTIFICATION_KEYS.includes(shortName as NotificationKey)) {
    console.debug("Notification key found for name: " + shortName);
    return shortName as NotificationKey;
  }
  throw new Error("Notification key not found for name: " + name);
};

export function notificationText(notification: any) {
  try {
    const key = getKeyFromName(notification.name);
    return NOTIFICATION_TEXTS[key];
  } catch {
    return `Text for notification key not found: ${notification.name}`;
  }
}

const keysForLoanName: NotificationKey[] = [
  "created_loan_contact",
  "deleted_loan_contact",
  "updated_loan",
  "requested_funds",
  "verified_funds",
  "created_item_contact",
  "created_draw_request",
  "updated_draw_request",
  "uploaded_document",
  "draw_due_soon",
];

const keysForPersonName: NotificationKey[] = ["kyc_verified", "onboarded", "invited_lender"];

export function notificationSubtext(notification: any): string {
  try {
    const { name, event } = notification;
    const notificationKey = getKeyFromName(name);

    if (keysForLoanName.includes(notificationKey)) {
      return event?.loan?.name ?? "Loan name not found";
    }
    if (keysForPersonName.includes(notificationKey)) {
      return `${event.user.first_name} ${event.user.last_name}`;
    }
    return ``;
  } catch {
    return "No notification text added yet for: " + notification.name;
  }
}

const MAX_SHOW = 3;

const NotificationsList = ({
  notifications,
  dismissNotification,
}: {
  notifications: any[];
  dismissNotification: (id: string) => void;
}) => {
  return (
    <UnorderedList listStyleType="none">
      {notifications
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .slice(0, MAX_SHOW)
        .map(notification => (
          <ListItem
            key={notification.id}
            borderBottomWidth="1px"
            borderBottomColor="gray.50"
            py="15px"
          >
            <Flex>
              <Avatar w="40px" h="40px" mr="15px" name="Lend Tech" />
              <Flex flexDir="column" flexGrow="1">
                <Text fontSize="13px" fontWeight="500" lineHeight="20px">
                  {notificationText(notification)}
                </Text>
                <Flex
                  flexGrow="1"
                  justifyContent="space-between"
                  color="gray.200"
                  fontSize="12px"
                  lineHeight="20px"
                  fontWeight="500"
                >
                  <Text>
                    {moment(notification.created_at).fromNow()} &middot;{" "}
                    {notificationSubtext(notification)}
                  </Text>
                  <Button
                    onClick={() => dismissNotification(notification.id)}
                    size="xs"
                    fontWeight="500"
                    variant="link"
                    _hover={{
                      textDecoration: "none",
                      color: "black",
                    }}
                  >
                    Dismiss
                  </Button>
                </Flex>
              </Flex>
            </Flex>
          </ListItem>
        ))}
      <ListItem>
        <Link to="/notifications">
          <Text
            w="100%"
            textAlign="center"
            color="gray.200"
            fontSize="12px"
            lineHeight="40px"
            fontWeight="500"
            mt="10px"
          >
            View All ({notifications.length})
          </Text>
        </Link>
      </ListItem>
      {/* {notifications.length > MAX_SHOW && (
        
      )} */}
    </UnorderedList>
  );
};

export default NotificationsList;
